import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '~env';
import { PaymentBillSource, PaymentReceipt } from './payment.model';

@Injectable({
  providedIn: 'root',
})
export class PaymentCommandService {
  private readonly registrarPaymentApi = environment.registrarPaymentApi;
  private readonly legalEntityV3CommandApi = environment.legalEntityV3CommandApi;
  private readonly disciplinaryBoardApi = environment.disciplinaryBoardApi;
  private readonly farpCommandApi = environment.farpCommandApi;
  private readonly anonymousPaymentHeaders = new HttpHeaders({
    'X-Api-Key': environment.anonymousPaymentApiKey,
  });

  constructor(private http: HttpClient) {}

  proceedCustomPayment(paymentData: {
    dataKey: string;
    nameUnderCard: string;
    amount: string;
    billId: string;
    source: PaymentBillSource;
    payerId?: string;
  }): Observable<PaymentReceipt> {
    switch (paymentData.source) {
      case PaymentBillSource.Student:
        return this.http.post<PaymentReceipt>(
          `${this.registrarPaymentApi}/payment/student/proceed-custom-payment`,
          {
            dataKey: paymentData.dataKey,
            nameUnderCard: paymentData.nameUnderCard,
            amount: paymentData.amount,
            billId: paymentData.billId,
          },
          { headers: this.anonymousPaymentHeaders }
        );
      case PaymentBillSource.Member:
        if (!paymentData.payerId) {
          return throwError(() => new Error('Member ID is required for member bill payment'));
        }
        return this.http.post<PaymentReceipt>(
          `${this.registrarPaymentApi}/Payment/${paymentData.payerId}/proceed-custom-payment`,
          {
            dataKey: paymentData.dataKey,
            nameUnderCard: paymentData.nameUnderCard,
            amount: paymentData.amount,
            billId: paymentData.billId,
          },
          { headers: this.anonymousPaymentHeaders }
        );
      case PaymentBillSource.LegalEntity:
        if (!paymentData.payerId) {
          return throwError(() => new Error('Member ID is required for legal entity bill payment'));
        }
        return this.http
          .post<Record<string, unknown>>(
            `${this.legalEntityV3CommandApi}/Payment/${paymentData.payerId}/proceed-custom-payment`,
            {
              billId: paymentData.billId,
              amount: Number(paymentData.amount),
              data_key: paymentData.dataKey,
              nameUnderCard: paymentData.nameUnderCard,
            },
            { headers: this.anonymousPaymentHeaders }
          )
          .pipe(map((response) => this.mapConfirmationToPaymentReceipt(this.unwrapCommandApiResponse(response))));
      case PaymentBillSource.DisciplinaryBoard:
        return this.http
          .post<Record<string, unknown>>(
            `${this.disciplinaryBoardApi}/Financial/proceed-custom-payment`,
            {
              billId: paymentData.billId,
              amount: Number(paymentData.amount),
              dataKey: paymentData.dataKey,
              nameUnderCard: paymentData.nameUnderCard,
            },
            { headers: this.anonymousPaymentHeaders }
          )
          .pipe(map((response) => this.mapConfirmationToPaymentReceipt(this.unwrapCommandApiResponse(response))));
      case PaymentBillSource.FarpOdq:
        return this.http
          .post<Record<string, unknown>>(
            `${this.farpCommandApi}/Payment/franchise/proceed-custom-payment`,
            {
              billId: paymentData.billId,
              amount: Number(paymentData.amount),
              dataKey: paymentData.dataKey,
              nameUnderCard: paymentData.nameUnderCard,
            },
            { headers: this.anonymousPaymentHeaders }
          )
          .pipe(map((response) => this.mapConfirmationToPaymentReceipt(this.unwrapCommandApiResponse(response))));
      default:
        return throwError(
          () => new Error(`Online payment is not yet supported for source: ${paymentData.source}`)
        );
    }
  }

  private unwrapCommandApiResponse(response: Record<string, unknown>): Record<string, unknown> {
    const wrapped = response as {
      content?: Record<string, unknown>;
      Content?: Record<string, unknown>;
    };
    return (wrapped.content ?? wrapped.Content ?? response) as Record<string, unknown>;
  }

  private mapConfirmationToPaymentReceipt(response: Record<string, unknown>): PaymentReceipt {
    return {
      orderId: String(response.orderId ?? response.OrderId ?? ''),
      date: String(response.date ?? response.Date ?? ''),
      amount: Number(response.amount ?? response.Amount ?? 0),
      monerisCardType: String(response.monerisCardType ?? response.MonerisCardType ?? ''),
      creditCardNumber: String(response.creditCardNumber ?? response.CreditCardNumber ?? ''),
      authorizationNumber: String(response.authorizationNumber ?? response.AuthorizationNumber ?? ''),
    };
  }
}
